import React, { ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface CustomTooltipProps {
  text: string;
  children?: ReactNode;
  alwaysShow?: boolean;
}

export const CustomTooltip: React.FC<CustomTooltipProps> = ({ text, children, alwaysShow = false }) => {
  const [show, setShow] = useState(alwaysShow);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top + window.scrollY - 45,
        left: rect.left + window.scrollX + rect.width / 2,
      });
    }
  };

  const handleMouseEnter = () => {
    if (text.trim()) {
      setShow(true);
      updatePosition();
    }
  };

  const handleMouseLeave = () => {
    // Only close on mouse leave if not in alwaysShow mode
    if (!alwaysShow) {
      setShow(false);
    }
  };

  React.useEffect(() => {
    if (alwaysShow) {
      setShow(true);
      updatePosition();
    } else {
      setShow(false);
    }
  }, [alwaysShow]);

  return (
    <>
      {children && (
        <div
          ref={triggerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className="inline-block"
        >
          {children}
        </div>
      )}

      {show &&
        createPortal(
          <div
            className="fixed pointer-events-none"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              transform: 'translateX(-50%)',
              zIndex: 99999,
            }}
          >
            <div
              className="font-sans font-medium text-xs whitespace-nowrap flex items-center justify-center"
              style={{
                backgroundColor: '#F8F8F8',
                border: '1px solid #DFDFDF',
                color: '#717171',
                fontSize: '13px',
                letterSpacing: '1px',
                padding: '0 20px',
                height: '30px',
              }}
            >
              {text}
              <div
                style={{
                  position: 'absolute',
                  bottom: '-5px',
                  width: '8px',
                  height: '8px',
                  borderBottom: '1px solid #dfdfdf',
                  borderRight: '1px solid #dfdfdf',
                  backgroundColor: '#f8f8f8',
                  left: '50%',
                  marginLeft: '-4px',
                  transform: 'rotate(45deg)',
                }}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default CustomTooltip;
